'use client'
import { Grid, Pagination } from '@achieve/ascend'
import Section from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { _get, preserveQueryParams, sanitizeUrlParams } from 'utils/shared'
import { getProductAndAddDefaultUtmsToLink } from 'utils/queryManipulation'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import styles from './PaginatedArticlesList.module.scss'
import { Eyebrow } from 'components/Eyebrow'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getBaseURL } from 'utils/shared/filter'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import AppStoreButtons from 'components/AppStoreButtons/AppStoreButtons'
import { ArticlesFilterContext } from 'providers/ArticlesFilterProvider'
import { getSlug, normalizeCategoryName } from 'utils/conversions'
import { Image } from 'components/Image'
import { PAGE_LIMIT_ARTICLES_DEFAULT } from 'services/contentful/constants'
import dynamic from 'next/dynamic'
const AchieveSkeleton = dynamic(() => import('components/AchieveSkeleton/AchieveSkeleton'))
import { mainProductConfig } from 'constants/mainProductConfig'
import { PaginationItem } from '@achieve/ascend'
import Link from 'next/link'
import {
  getPaginatedCategoryHref,
  getPaginatedHrefClean,
  PAGE_STATE_PARAMS,
} from './paginationUrls'

function deleteDoubleQuotes(filterString) {
  return filterString.split('"').join('')
}

function PaginatedArticlesList({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const { query, push, dynamicPathname, fullAsPath, params } = useAppRouter()
  const categoryname = params.categoryname
  const routePid = params.pid
  const { cardCta, listArticles } = useMemoizedContentGetter(content, ['cardCta', 'listArticles'])
  const config = _get(content, 'fields.additionalConfiguration.jsonContent', '')

  const { state, dispatch } = useContext(ArticlesFilterContext)
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState()

  const resolvedPage = useMemo(() => {
    const raw = routePid ?? query?.page
    const n = parseInt(Array.isArray(raw) ? raw[0] : raw, 10)
    if (Number.isFinite(n) && n >= 1) return n
    return state?.page ? parseInt(state.page, 10) || 1 : 1
  }, [routePid, query?.page, state?.page])

  const paginationPageCount = useMemo(
    () =>
      Math.ceil(
        parseInt(state?.articlesObject?.total, 10) /
          parseInt(config?.pageLimit ?? PAGE_LIMIT_ARTICLES_DEFAULT, 10)
      ),
    [state?.articlesObject?.total, config?.pageLimit]
  )

  const basePath = useMemo(() => fullAsPath.split('?')[0], [fullAsPath])

  const isSearchRoute = useMemo(() => {
    const normalizedPath = basePath.replace(/\/$/, '')
    return normalizedPath === '/learn/search'
  }, [basePath])

  const getPageHrefClean = useCallback(
    (page) => {
      if (isSearchRoute) {
        const url = new URLSearchParams()
        if (page > 1) {
          url.set('page', String(page))
        }
        for (const key of PAGE_STATE_PARAMS) {
          const rawValue = query?.[key]
          const value = Array.isArray(rawValue) ? rawValue[0] : rawValue
          if (value != null && value !== '') {
            url.set(key, value)
          }
        }
        const qs = url.toString()
        return qs ? `${basePath}?${qs}` : basePath
      }

      return getPaginatedHrefClean(basePath, page, query)
    },
    [query, basePath, isSearchRoute]
  )

  const getPageHrefFull = useCallback(
    (page) => {
      if (isSearchRoute) {
        const url = new URLSearchParams(query)
        if (page > 1) {
          url.set('page', String(page))
        } else {
          url.delete('page')
        }
        const qs = url.toString()
        const rawHref = qs ? `${basePath}?${qs}` : basePath
        return sanitizeUrlParams(
          getProductAndAddDefaultUtmsToLink(preserveQueryParams(rawHref, query))
        )
      }

      const rawHref = getPaginatedCategoryHref(basePath, page, query)
      if (page === 1) return rawHref

      return sanitizeUrlParams(
        getProductAndAddDefaultUtmsToLink(preserveQueryParams(rawHref, query))
      )
    },
    [query, basePath, isSearchRoute]
  )

  const initialRenderRef = useRef(true)

  const stateRef = useRef(state)
  stateRef.current = state
  const queryRef = useRef(query)
  queryRef.current = query
  const pushRef = useRef(push)
  pushRef.current = push
  const fullAsPathRef = useRef(fullAsPath)
  fullAsPathRef.current = fullAsPath

  const getBody = useCallback(() => {
    const currentState = stateRef.current
    const currentQuery = queryRef.current

    let body = {
      content_type: listArticles?.contentType,
      pageLimit: config?.pageLimit ?? PAGE_LIMIT_ARTICLES_DEFAULT,
      page: currentState?.page ?? routePid ?? currentQuery.page ?? 1,
      filterFields: {
        contentType: listArticles?.contentTypeArticle,
        showContent: true,
      },
      orderBy: currentState?.orderBy,
    }

    if (categoryname && categoryname !== '*' && !listArticles?.isSubcategory) {
      body.category = listArticles?.categoryName
    }

    if (currentState?.keywordsFilter !== '') {
      body.keywords = currentState?.keywordsFilter
    }

    if (listArticles?.allPage) {
      delete body.category
    }

    if (listArticles?.isSubcategory) {
      body.category = null
      body.subcategory = listArticles?.categoryName
    }

    return body
  }, [listArticles, config?.pageLimit, categoryname, routePid])

  const fetchArticlesSearch = useCallback(
    async ({ dispatch, signal }) => {
      try {
        setLoading(true)
        const body = getBody()
        const currentQuery = queryRef.current
        const url = new URLSearchParams(currentQuery)
        if (body.keywords) {
          const nextSearch = encodeURI(body.keywords)
          const currentUrlSearch = url.get('search')
          // If the URL `search` param differs from the keywords already in state
          // (e.g. user submitted a new term via the SearchBox), treat the URL as
          // authoritative and sync state to it instead of overwriting the URL
          // with the stale keywords from state.
          if (
            currentUrlSearch &&
            currentUrlSearch !== body.keywords &&
            currentUrlSearch !== nextSearch
          ) {
            dispatch({ type: 'SET_KEYWORDS', payload: currentUrlSearch })
            body.keywords = currentUrlSearch
          } else if (currentUrlSearch !== nextSearch) {
            url.set('search', nextSearch)
            pushRef.current(`${fullAsPathRef.current.split('?')[0]}?${url.toString()}`)
          }
        } else {
          if (currentQuery.search) {
            dispatch({ type: 'SET_KEYWORDS', payload: currentQuery.search })
            body.keywords = currentQuery.search
          }
        }

        const res = await fetch('/api/contentful/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        })
        const contentfulJson = await res.json()
        if (signal?.aborted) return
        dispatch({
          type: 'SET_ARTICLES',
          payload: { articles: contentfulJson?.items, total: contentfulJson?.total },
        })
        setLoading(false)
      } catch (error) {
        if (error.name === 'AbortError') return
        dispatch({ type: 'SET_ARTICLES', payload: { articles: [] } })
        setLoading(false)
      }
    },
    [getBody]
  )

  const fetchArticles = useCallback(
    async ({ dispatch, signal }) => {
      try {
        setLoading(true)
        const body = getBody()
        const res = await fetch('/api/contentful/fetch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        })
        const contentfulJson = await res.json()
        if (signal?.aborted) return
        dispatch({
          type: 'SET_ARTICLES',
          payload: { articles: contentfulJson?.items, total: contentfulJson?.total },
        })
        setLoading(false)
      } catch (error) {
        if (error.name === 'AbortError') return
        dispatch({ type: 'SET_ARTICLES', payload: { articles: [] } })
        setLoading(false)
      }
    },
    [getBody]
  )

  useEffect(() => {
    dispatch({ type: 'SET_PAGE', payload: routePid ?? query?.page ?? 1 })
  }, [dispatch, routePid, query?.page])

  const isStateReady = Object.keys(state).length > 0

  useEffect(() => {
    if (!isStateReady) return

    const controller = new AbortController()
    const { signal } = controller

    if (initialRenderRef.current) {
      initialRenderRef.current = false
      if (query.search) {
        fetchArticlesSearch({ dispatch, signal })
      }
      return () => controller.abort()
    }

    if (query.search) {
      fetchArticlesSearch({ dispatch, signal })
    } else {
      fetchArticles({ dispatch, signal })
    }
    return () => controller.abort()
  }, [
    isStateReady,
    routePid,
    query.page,
    query.search,
    categoryname,
    state?.orderBy,
    state?.keywordsFilter,
    dispatch,
    fetchArticlesSearch,
    fetchArticles,
  ])

  const RenderCard = ({
    idx,
    title,
    primaryCategory,
    hook,
    slug,
    mainImage,
    variant = 'default',
    ctaSection,
    isVideo,
  }) => {
    const isCtaVariant = variant == 'cta'
    const CardContent = (
      <div className={styles['card-container']}>
        <Grid container columnSpacing={2} className={styles['card']}>
          <Grid size={{ xs: isCtaVariant ? 12 : 4, md: isCtaVariant ? 12 : 3, lg: 2 }}>
            <div className={styles['image-container']}>
              {mainImage && (
                <MediaImageStandard
                  alt="render-card-image"
                  content={mainImage}
                  fetchPriority="high"
                  preload={true}
                  aspectRatio={16 / 9}
                />
              )}
              {isVideo && (
                <div className={styles['play-button']}>
                  <Image alt={'Play Button'} height={64} src="/play-btn.svg" width={64} />
                </div>
              )}
            </div>
          </Grid>
          <Grid
            size={{ xs: isCtaVariant ? 12 : 8, md: isCtaVariant ? 12 : 9, lg: 10 }}
            display={'flex'}
            flexDirection={'column'}
            gap={1}
          >
            <Eyebrow
              className={`${styles['card-eyebrow']} text-left!`}
              content={primaryCategory}
              variant={'labelSm'}
            />
            <Typography
              content={title}
              variant={'labelSm'}
              className={styles['card-title']}
              data-testid={`card-title`}
            />
            <Typography content={hook} variant="labelXs" className={styles['card-hook']} />
            {ctaSection}
          </Grid>
        </Grid>
      </div>
    )
    return (
      <Grid size={{ xs: 12 }} data-variant={variant}>
        {isCtaVariant ? (
          <>{CardContent}</>
        ) : (
          <AchieveLink
            track={{
              list_name: 'Card Article',
              click_position: idx,
              click_id: title,
              click_text: title,
              nav_link_section: 'Content Grid',
              track_event: 'page_nav_click',
              event_action: 'link_click',
              event_type: 'link_click',
              sectionVariationObject,
            }}
            className={styles['anchor-links']}
            href={`/${getBaseURL()}/${getSlug(primaryCategory)}/${slug}`}
            data-testid="article-list-card-link"
          >
            {CardContent}
          </AchieveLink>
        )}
      </Grid>
    )
  }

  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const changeOrderSearch = (order) => {
    dispatch({ type: 'SET_ORDER_BY', payload: order })
  }

  useEffect(() => {
    setTitle(
      dynamicPathname.indexOf('/learn/search') !== -1
        ? state?.keywordsFilter
          ? `Search results for "${deleteDoubleQuotes(state?.keywordsFilter)}"`
          : `All Articles`
        : `All ${
            categoryname != 'all' ? normalizeCategoryName(categoryname) : 'Financial Education'
          } Articles`
    )
  }, [categoryname, state?.keywordsFilter, dynamicPathname])

  return (
    <Section id="article_list" data-testid="articles-list-section">
      <Grid container marginBottom={4} marginTop={4} rowGap={2}>
        <Grid size={{ xs: 12 }}>
          <Grid container rowGap={2}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Typography content={title} variant="headingMd" data-testid="article-list-title" />
            </Grid>
            <Grid
              size={{ xs: 12, lg: 4 }}
              display={'flex'}
              flexDirection={'row'}
              columnGap={2}
              justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            >
              <AchieveLink
                noLink
                className={styles['link-filter']}
                data-active={state?.orderBy === 'popular'}
                onClick={() => changeOrderSearch('popular')}
                data-testid="article-list-popular-filter-link"
              >
                <Typography
                  content={categoryname == 'search' ? 'Most Relevant' : 'Most Popular'}
                  variant="labelXs"
                  data-testid="article-list-popular"
                />
              </AchieveLink>
              <AchieveLink
                noLink
                className={styles['link-filter']}
                data-active={state?.orderBy === 'recent'}
                onClick={() => changeOrderSearch('recent')}
                data-testid="article-list-popular-recent-link"
              >
                <Typography
                  content="Most Recent"
                  variant="labelXs"
                  data-testid="article-list-recent"
                />
              </AchieveLink>
            </Grid>
          </Grid>
        </Grid>
        {state?.articlesObject?.articles && state?.articlesObject?.articles.length > 0 && !loading && (
          <Grid size={{ xs: 12 }}>
            <Grid container data-testid="articles-list-cards-container">
              {state?.articlesObject?.articles.map((article, idx) => {
                if (!article) {
                  return null
                }
                const itemsRender = [
                  <RenderCard
                    idx={idx}
                    slug={article.slug}
                    key={`RenderCard-${idx}`}
                    title={article.title}
                    hook={article.hook}
                    primaryCategory={article.primaryCategory}
                    mainImage={article?.mainImage?.mediaContent ?? article?.mainImage?.thumbnail}
                    isVideo={article?.mainImage?.__typename === 'VideoPlayer'}
                  />,
                ]
                if (config?.cardCtaPosition) {
                  if (idx + 1 === config.cardCtaPosition)
                    itemsRender.push(
                      <RenderCard
                        key={`RenderCardCTA-${idx}`}
                        title={cardCta.cardTitleText}
                        hook={cardCta.cardText}
                        primaryCategory={cardCta.eyebrow}
                        mainImage={cardCta?.cardImage}
                        variant="cta"
                        ctaSection={
                          <AppStoreButtons
                            appleButton={cardCta.linkApple}
                            googleButton={cardCta.linkAndroid}
                            appleImage={cardCta.imageApple}
                            googleImage={cardCta.imageAndroid}
                            navLinkSection="Article"
                          />
                        }
                      />
                    )
                }
                return itemsRender
              })}
            </Grid>
          </Grid>
        )}
        {loading && (
          <div className={styles['skeleton-list']}>
            {Array.from({ length: config?.pageLimit ?? PAGE_LIMIT_ARTICLES_DEFAULT }).map(
              (_, idx) => (
                <AchieveSkeleton
                  key={idx}
                  width={'100%'}
                  height={idx + 1 === config?.cardCtaPosition ? 400 : 130}
                />
              )
            )}
          </div>
        )}
        <Grid
          size={{ xs: 12 }}
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'center'}
          gap={2}
        >
          {paginationPageCount > 1 && (
            <Grid item className={styles['pagination']}>
              <TrackingWrapper
                track={{
                  list_name: 'Pagination Event',
                  click_id: `${
                    mainProductConfig[siteEnv || 'achieve']?.['MainProduct']
                  } | Pagination`,
                  click_position: 'Pagination',
                  click_text: 'Pagination',
                  nav_link_section: 'Pagination',
                  track_event: 'ui_click',
                  event_action: 'ui_click',
                  event_type: 'ui_click',
                  sectionVariationObject,
                }}
              >
                <Pagination
                  color="primary"
                  className={styles['pagination-items']}
                  data-testid="article-grid-pagination"
                  count={paginationPageCount}
                  page={resolvedPage}
                  renderItem={(item) => {
                    if (
                      item.disabled ||
                      item.page == null ||
                      item.type === 'start-ellipsis' ||
                      item.type === 'end-ellipsis'
                    ) {
                      return <PaginationItem {...item} />
                    }
                    const pageNum = item.page
                    if (pageNum < 1 || pageNum > paginationPageCount) {
                      return <PaginationItem {...item} />
                    }
                    return (
                      <PaginationItem
                        component={Link}
                        href={getPageHrefClean(pageNum)}
                        prefetch={false}
                        scroll={false}
                        {...item}
                        onClick={(e) => {
                          // Respect modifier/middle clicks: let the browser
                          // handle them with the clean href that's already
                          // rendered in the DOM (same behavior as AchieveLink
                          // for non-interacted state).
                          const isModifiedClick =
                            e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1
                          if (isModifiedClick) return
                          e.preventDefault()
                          push(getPageHrefFull(pageNum))
                          item?.onClick?.(e)
                        }}
                      />
                    )
                  }}
                />
              </TrackingWrapper>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Section>
  )
}
export { PaginatedArticlesList }
export default PaginatedArticlesList
